import {
  isDefinedArray,
  isDefinedString,
  isNullOrUndefined,
  isPositiveNumber,
  reduce
} from "et3-type-guards";
import { memoize } from "lodash";
import { computed, observable, runInAction } from "mobx";
import {
  StringOrNumber,
  TConversationPropKeys,
  TConversationsProps,
  TCorePage,
  TGender,
  TGetKhattabaClients,
  TKhattabatClientCategory,
  TKhattabatConversations,
  TKhattabatMessage,
  TMediaType,
  TMessagesType,
  TReactivateOneOTwoConversationProps,
  TSelectionPages,
  TalkhattabatConversationType,
  TKhattabatApplicationStatus,
  E_KHATTABAT_APPLICATION_STATUS,
  TClientPageType,
  E_CLIENT_PAGE
} from "../../Types";
import { apiCallGetKhattabaClients } from "../../api/khattbat-api/api-call-get-khattaba-clients";
import getCoreUIStore from "../core/core-ui-store";
import getCorePopUpStore from "../core/core-popup-store";
import { Ti18nKeys } from "../../constants/i18n";
import { apiCallGetConversations } from "../../api/khattbat-api/api-call-get-conversations";
import { apiCallSendOneOOneMessage } from "../../api/khattbat-api/api-call-send-one-o-one-message";
import {
  ALLOWED_UPLOADED_IMAGES_TYPES,
  DEFAULT_TIME,
  MAX_APP_MESSAGE_LENGTH,
  MAX_UPLOADED_IMAGE_SIZE_IN_MB
} from "../../constants/input-entries-constants";
import { apiCallCreateOneOTwo } from "../../api/khattbat-api/api-call-create-one-o-two";
import { apiCallSendOneOTwoMessage } from "../../api/khattbat-api/api-call-send-one-o-two-message";
import getKhattabatFilterstore from "./filter-store";
import getKhattabatSelectsStore from "./selects-store";
import { apiCallSetOneOOneMessagesSeen } from "../../api/khattbat-api/api-call-set-one-o-one-messages-seen";
import { getKhattabatResultsStore } from "./results-store";
import { fileOpen } from "browser-fs-access";
import { apiCallSendOneOOneMidea } from "../../api/khattbat-api/api-call-send-one-o-one-midea";
import { apiCallSendOneOTwoMidea } from "../../api/khattbat-api/api-call-send-one-o-two-midea";
import { imageHelper } from "../../helpers/image-helper";
import getCoreTimingStore from "../core/core-timing-store";
import {
  KHATTABAT_REACTIVATION_DELAY_DAYS,
  KHATTABAT_ONE_O_TWO_MIN_QUALITY_MESSAGES
} from "../../constants/core-constants";
import { apiCallReactivateOneOTwo } from "../../api/khattbat-api/api-call-reactivate-one-o-two";
import { apiCallUpdateOneOTwoQuality } from "../../api/khattbat-api/api-call-update-one-o-two-quality";
import {
  createDateFromSpecificDateString,
  getDaysDifference,
  getDefaultDate
} from "../../helpers/et3-date-helper";
import { apiCallReactivateKhatabaClient } from "../../api/khattbat-api/api-call-reactivate-khataba-client";
import getCorePersonalStore from "../core/core-personal-store";
import { getKhattabaAiStore } from "./khattaba-ai-store";

class KhattabatStore {
  clients = observable.box<TGetKhattabaClients[]>([]);
  conversationsProps = observable.map<string, TConversationsProps>([]);
  clientConversations = observable.map<
    string,
    TKhattabatConversations & { shouldShowConversations: boolean }
  >([]);
  conversationsClientType = observable.box<TKhattabatClientCategory>("PAID");
  allAvailableClientsIds = observable.box<number[]>([]);
  reactivateOneOTwoConversationProps =
    observable.box<TReactivateOneOTwoConversationProps>({
      conversationID: "",
      alkhattabaUUID: "",
      userUUID: "",
      oneOOneCId: ""
    });
  imageToBeUploadedBase64 = observable.box<string>("");
  clientToBeReactivatedDetails = observable.box<{
    userID: number;
    alkhattabaUUID: string;
    clientUUID: string;
    clientConversationID: string;
  }>({
    userID: -1,
    alkhattabaUUID: "",
    clientUUID: "",
    clientConversationID: ""
  });
  userQuizStatusToBeUpdated = observable.box<{
    userID: number;
    newStatus: TKhattabatApplicationStatus;
  }>({ userID: -1, newStatus: E_KHATTABAT_APPLICATION_STATUS.REJECTED });
  clientPageType = observable.box<TClientPageType>(E_CLIENT_PAGE.BREACHE_LEVEL);

  getClientPageType = computed(() => {
    const isAiKhattaba = getCorePersonalStore().isKhattabaAiAdmin.get();
    if (isAiKhattaba) {
      return E_CLIENT_PAGE.ALL;
    }
    return this.clientPageType.get();
  });

  getRequiresAttentionClients = computed((): TGetKhattabaClients[] => {
    const clients: TGetKhattabaClients[] = this.clients.get();
    const isAiKhattaba = getCorePersonalStore().isKhattabaAiAdmin.get();
    if (!isDefinedArray(clients) || isAiKhattaba) {
      return [];
    }

    return (
      clients
        // filter only clients that meet any of the 3 attention conditions
        .filter(
          (client) =>
            client.clientIsAlmostExpiredAndNo102Quality ||
            client.hasUnreadMessagesSinceMoreThan18Hours ||
            client.hasNoRCMessageSinceMoreThan54Hours
        )
        // sort based on the required ordering
        .sort((a, b) => {
          // 1. Client expiry in < 7 days AND no quality 102
          if (
            a.clientIsAlmostExpiredAndNo102Quality &&
            !b.clientIsAlmostExpiredAndNo102Quality
          )
            return -1;
          if (
            !a.clientIsAlmostExpiredAndNo102Quality &&
            b.clientIsAlmostExpiredAndNo102Quality
          )
            return 1;

          // 2. Client has unread messages > 18 hours
          if (
            a.hasUnreadMessagesSinceMoreThan18Hours &&
            !b.hasUnreadMessagesSinceMoreThan18Hours
          )
            return -1;
          if (
            !a.hasUnreadMessagesSinceMoreThan18Hours &&
            b.hasUnreadMessagesSinceMoreThan18Hours
          )
            return 1;

          // 3. RC has not sent message > 54 hours
          if (
            a.hasNoRCMessageSinceMoreThan54Hours &&
            !b.hasNoRCMessageSinceMoreThan54Hours
          )
            return -1;
          if (
            !a.hasNoRCMessageSinceMoreThan54Hours &&
            b.hasNoRCMessageSinceMoreThan54Hours
          )
            return 1;

          return 0;
        })
    );
  });

  setClientPageType = (type: TClientPageType) => {
    getKhattabatFilterstore().resetFilters();
    runInAction(() => {
      this.clientPageType.set(type);
    });
  };

  setReactivateOneOTwoConversationProps = (
    props: TReactivateOneOTwoConversationProps
  ) => {
    runInAction(() => {
      this.reactivateOneOTwoConversationProps.set(props);
    });
  };

  setImageToBeUploadedBase64 = (base64: string) => {
    runInAction(() => {
      this.imageToBeUploadedBase64.set(base64);
    });
  };

  setAllAvailableClientsIds = (clients: TGetKhattabaClients[]) => {
    const Ids = clients.map((client) => client.userID);

    runInAction(() => {
      const currentIds = this.allAvailableClientsIds.get();
      Ids.forEach((id) => {
        if (!currentIds.includes(id)) {
          currentIds.push(id);
        }
      });
      this.allAvailableClientsIds.set(currentIds);
    });
  };

  setConversationsClientType = (clientType: TKhattabatClientCategory) => {
    runInAction(() => {
      this.conversationsClientType.set(clientType);
    });
  };

  setKhattaClients = (clients: TGetKhattabaClients[]) => {
    runInAction(() => {
      this.clients.set(clients);
    });
  };

  setClientToBeReactivatedDetails = ({
    userID,
    alkhattabaUUID,
    clientConversationID,
    clientUUID
  }: {
    userID: number;
    alkhattabaUUID: string;
    clientUUID: string;
    clientConversationID: string;
  }) => {
    runInAction(() => {
      this.clientToBeReactivatedDetails.set({
        userID,
        alkhattabaUUID,
        clientConversationID,
        clientUUID
      });
    });
  };

  setUserQuizStatusToBeUpdated = ({
    userID,
    newStatus
  }: {
    userID: number;
    newStatus: TKhattabatApplicationStatus;
  }) => {
    runInAction(() => {
      this.userQuizStatusToBeUpdated.set({ userID, newStatus });
    });
  };

  updateClients = (clients: TGetKhattabaClients[]) => {
    const currentClients = this.clients.get();
    clients.forEach((newClient) => {
      const existingClientIndex = currentClients.findIndex(
        (client) => client.userUUID === newClient.userUUID
      );
      if (isPositiveNumber(existingClientIndex, true)) {
        currentClients[existingClientIndex] = newClient;
      } else {
        currentClients.push(newClient);
      }
    });
    this.setKhattaClients(currentClients);
  };

  getKhattabaClients = async ({
    gender,
    pageName
  }: {
    gender: TGender;
    pageName: TCorePage;
  }) => {
    getCoreUIStore().setIsLoading(pageName, true);
    const { status, code, data } = await apiCallGetKhattabaClients({
      gender,
      shouldUpdateUnread: false
    });
    getCoreUIStore().setIsLoading(pageName, false);
    if (status === "OK") {
      const activeKhattabat = reduce(() => data, []);
      this.setKhattaClients(activeKhattabat);
      getKhattabatSelectsStore().initializeAndClearMarksStateWithClients(
        "clientPage"
      );
      getCoreTimingStore().initializeKhattabatPageMonitoring(pageName);
      this.setAllAvailableClientsIds(activeKhattabat);
      return;
    } else {
      getCorePopUpStore().showAlertMessage(code);
    }
  };

  getUnreadKhattabaClients = async ({ gender }: { gender: TGender }) => {
    const { status, code, data } = await apiCallGetKhattabaClients({
      gender,
      shouldUpdateUnread: true
    });
    if (status === "OK") {
      const activeKhattabat = reduce(() => data, []);
      this.updateClients(activeKhattabat);
      this.setAllAvailableClientsIds(activeKhattabat);
      return;
    } else {
      getCorePopUpStore().showAlertMessage(code);
    }
  };

  createConversationProps = (conversationID: string) => {
    const isConversationPropsExist = !isNullOrUndefined(
      this.conversationsProps.get(conversationID)
    );
    runInAction(() => {
      if (isConversationPropsExist) {
        return;
      }

      this.conversationsProps.set(conversationID, {
        isOpened: "CLOSED",
        messageField: "",
        insertUserIdField: -1
      });
    });
  };

  updateConversationProp = async ({
    conversationID,
    propKey,
    value
  }: {
    conversationID: string;
    propKey: TConversationPropKeys;
    value: StringOrNumber;
  }): Promise<void> => {
    runInAction(async () => {
      switch (propKey) {
        case "messageField":
          if (typeof value === "string") {
            if (value.length > MAX_APP_MESSAGE_LENGTH) {
              return null;
            }
            const isFirstCharacter = !isDefinedString(
              this.conversationsProps.get(conversationID)[propKey]
            );
            const finalizedValue = isFirstCharacter ? value.trim() : value;

            return (this.conversationsProps.get(conversationID)[propKey] =
              finalizedValue);
          }
          break;
        case "insertUserIdField":
          if (typeof value === "number") {
            return (this.conversationsProps.get(conversationID)[propKey] =
              value);
          }
          break;
        case "isOpened":
          const currentState = reduce(
            () => this.conversationsProps.get(conversationID).isOpened,
            "OPENED"
          );

          if (currentState === "OPENED") {
            return (this.conversationsProps.get(conversationID)[propKey] =
              "CLOSED");
          } else {
            return (this.conversationsProps.get(conversationID)[propKey] =
              "OPENED");
          }
        default:
          break;
      }
      return null;
    });
  };

  getConversationHeaderStatusValues = ({
    exitedByClient,
    exitedByMatch,
    exitedByClientDate,
    exitedByMatchDate,
    pendingInteraction
  }: {
    exitedByMatch: boolean;
    exitedByClient: boolean;
    exitedByClientDate: Date;
    exitedByMatchDate: Date;
    pendingInteraction: boolean;
  }): {
    conversationStatuskey: Ti18nKeys;
    conversationStatusImageName: string;
    conversationExitDate: Date;
  } => {
    let conversationStatuskey: Ti18nKeys = "ONGOING";
    let conversationStatusImageName = "green-ellipse";
    let conversationExitDate = createDateFromSpecificDateString("");

    if (pendingInteraction) {
      conversationStatusImageName = "orange-ellipse";
      conversationStatuskey = "PENDING_INTERACTION";
    }
    if (exitedByMatch && exitedByClient) {
      conversationStatusImageName = "red-ellipse";
      if (exitedByClientDate < exitedByMatchDate) {
        conversationStatuskey = "EXITED_BY_CLIENT";
        conversationExitDate = exitedByClientDate;
      } else {
        conversationStatuskey = "EXITED_BY_MATCH";
        conversationExitDate = exitedByMatchDate;
      }
    }

    if (exitedByMatch && !exitedByClient) {
      conversationStatusImageName = "red-ellipse";
      conversationStatuskey = "EXITED_BY_MATCH";
      conversationExitDate = exitedByMatchDate;
    }

    if (!exitedByMatch && exitedByClient) {
      conversationStatusImageName = "red-ellipse";
      conversationStatuskey = "EXITED_BY_CLIENT";
      conversationExitDate = exitedByClientDate;
    }

    return {
      conversationStatuskey,
      conversationExitDate,
      conversationStatusImageName
    };
  };

  getOneOOneConversationsId = ({
    alKhattabaUUID,
    userUUID
  }: {
    alKhattabaUUID: string;
    userUUID: string;
  }) => {
    const reducedAlkhattabaUUid = reduce(() => alKhattabaUUID, "");
    const reducedUserUUid = reduce(() => userUUID, "");
    const clientConversationsId = `${reducedAlkhattabaUUid}_${reducedUserUUid}`;
    return clientConversationsId;
  };

  clearClientConversations = ({
    clientConversationId
  }: {
    clientConversationId: string;
  }) => {
    runInAction(() => {
      this.clientConversations.get(clientConversationId).oneOOneCMessages = [];
      this.clientConversations.get(clientConversationId).oneOTwoMessages = {};
      this.clientConversations.get(clientConversationId).oneOOneCComments = [];
    });
  };

  updateClientConversations = ({
    clientConversations
  }: {
    clientConversations: TKhattabatConversations;
  }) => {
    const clientConversationId = clientConversations.oneOOneCId;

    const isConversationsExist = !isNullOrUndefined(
      this.clientConversations.get(clientConversationId)
    );
    runInAction(() => {
      const reducedOneOneCMessages = reduce(
        () => clientConversations.oneOOneCMessages,
        []
      );
      const reducedOneOneCComments = reduce(
        () => clientConversations.oneOOneCComments,
        []
      );
      const reducedOneOTwoMessages = reduce(
        () => clientConversations.oneOTwoMessages,
        {}
      );
      const reducedAlkhattabaUUID = reduce(
        () => clientConversations.alKhattabaUUID,
        ""
      );
      const reducedUserID = reduce(() => clientConversations.userID, -1);
      const reducedUserUUID = reduce(() => clientConversations.userUUID, "");
      const reducedOneOOneCId = reduce(
        () => clientConversations.oneOOneCId,
        ""
      );

      if (isConversationsExist) {
        this.clearClientConversations({ clientConversationId });
        this.clientConversations.get(clientConversationId).oneOOneCMessages =
          reducedOneOneCMessages;
        this.clientConversations.get(clientConversationId).oneOOneCComments =
          reducedOneOneCComments;
        this.clientConversations.get(clientConversationId).oneOTwoMessages =
          reducedOneOTwoMessages;
        this.clientConversations.get(
          clientConversationId
        ).shouldShowConversations = true;
      } else {
        this.clientConversations.set(clientConversationId, {
          oneOOneCMessages: reducedOneOneCMessages,
          oneOOneCComments: reducedOneOneCComments,
          oneOTwoMessages: reducedOneOTwoMessages,
          alKhattabaUUID: reducedAlkhattabaUUID,
          userUUID: reducedUserUUID,
          shouldShowConversations: true,
          userID: reducedUserID,
          oneOOneCId: reducedOneOOneCId
        });
      }
    });
  };
  updateOpenedClientConversations = async () => {
    const clientConversations = getKhattabatStore().clientConversations;
    for (const conversation of clientConversations.values()) {
      if (conversation.shouldShowConversations) {
        await getKhattabatStore().getClientConversations({
          alkhattabaUUID: conversation.alKhattabaUUID,
          userUUID: conversation.userUUID,
          shouldLoad: false,
          oneOOneCId: conversation.oneOOneCId
        });
      }
    }
  };

  getClientConversations = async ({
    alkhattabaUUID,
    userUUID,
    shouldLoad,
    oneOOneCId
  }: {
    alkhattabaUUID: string;
    userUUID: string;
    shouldLoad: boolean;
    oneOOneCId: string;
  }) => {
    getCoreUIStore().setIsLoading("KHATTABAT_CLIENTS", shouldLoad);
    const { status, code, data } = await apiCallGetConversations({
      alKhattabaUUID: alkhattabaUUID,
      userUUID,
      oneOOneCId
    });
    getCoreUIStore().setIsLoading("KHATTABAT_CLIENTS", false);
    if (status === "OK") {
      this.updateClientConversations({ clientConversations: data });
      return;
    } else {
      getCorePopUpStore().showAlertMessage(code);
    }
  };

  closeClientConversations = ({
    clientConversationsId
  }: {
    clientConversationsId: string;
  }) => {
    runInAction(() => {
      this.clientConversations.get(
        clientConversationsId
      ).shouldShowConversations = false;
    });
  };

  handleSendOneOOneMessageClient = ({
    message,
    conversationID,
    databaseCurrentTime,
    alkhattabaUUID,
    userUUID,
    userID,
    conversationType,
    messageType,
    oneOOneCId
  }: {
    message: string;
    conversationID: string;
    databaseCurrentTime: string;
    alkhattabaUUID: string;
    userUUID: string;
    userID: number;
    conversationType: TalkhattabatConversationType;
    messageType: TMessagesType;
    oneOOneCId: string;
  }) => {
    const isOneOneCConversation = conversationType === "101C";
    const isOneOneCComments = conversationType === "101C_COMMENTS";
    const isOneOneMComments = conversationType === "101M_COMMENTS";
    runInAction(() => {
      const isOneOOneConversationExist =
        isOneOneCConversation || isOneOneCComments
          ? !isNullOrUndefined(this.clientConversations.get(conversationID))
          : isPositiveNumber(
              getKhattabatResultsStore().findMatchIndexInConversationArray({
                parameter: "matchID",
                parameterValue: userID,
                matchesConversations:
                  getKhattabatResultsStore().matchesConversations.get()
              }),
              true
            );

      const oneOOneMessageToAdd: TKhattabatMessage = {
        isKhattabaMessage: 1,
        isClientMessage: 0,
        message,
        messageType,
        createdOn: databaseCurrentTime,
        isSeen: 1,
        senderGender: "f",
        conversationId: conversationID
      };
      if (!isOneOOneConversationExist) {
        if (isOneOneCConversation || isOneOneCComments) {
          this.clientConversations.set(conversationID, {
            oneOOneCMessages: [],
            oneOOneCComments: [],
            oneOTwoMessages: {},
            alKhattabaUUID: alkhattabaUUID,
            userUUID,
            userID,
            shouldShowConversations: true,
            oneOOneCId
          });
        } else {
          const index =
            getKhattabatResultsStore().findMatchIndexInConversationArray({
              parameter: "matchID",
              parameterValue: userID,
              matchesConversations:
                getKhattabatResultsStore().matchesConversations.get()
            });
          getKhattabatResultsStore().matchesConversations.get()[
            index
          ].messages = [];
        }
      }
      if (isOneOneCConversation) {
        this.clientConversations
          .get(conversationID)
          .oneOOneCMessages.push(oneOOneMessageToAdd);

        const isAiKhattaba = getCorePersonalStore().isKhattabaAiAdmin.get();
        if (!isAiKhattaba) {
          this.updateBreachLevelClientStats({
            alkhattabaUUID: alkhattabaUUID,
            clientUUID: userUUID,
            booleanKey: "hasNoRCMessageSinceMoreThan54Hours",
            booleanKeyNewValue: false
          });
        }
      } else if (isOneOneCComments) {
        this.clientConversations
          .get(conversationID.slice(0, -2))
          .oneOOneCComments.push(oneOOneMessageToAdd);
      } else if (isOneOneMComments) {
        const index =
          getKhattabatResultsStore().findMatchIndexInConversationArray({
            parameter: "matchID",
            parameterValue: userID,
            matchesConversations:
              getKhattabatResultsStore().matchesConversations.get()
          });
        getKhattabatResultsStore()
          .matchesConversations.get()
          [index].oneOOneMComments.push(oneOOneMessageToAdd);
      } else {
        const index =
          getKhattabatResultsStore().findMatchIndexInConversationArray({
            parameter: "matchID",
            parameterValue: userID,
            matchesConversations:
              getKhattabatResultsStore().matchesConversations.get()
          });
        getKhattabatResultsStore()
          .matchesConversations.get()
          [index].messages.push(oneOOneMessageToAdd);
        getKhattabatResultsStore().updateMatchConversationEnablementState({
          matchID: userID,
          oldState: true
        });
        getKhattabatResultsStore().updateMatchConversationDeletionState({
          matchID: userID,
          oldState: true
        });
      }

      getKhattabatFilterstore().update1o1MFilter();
    });
  };

  sendOneOMessageDatabase = async ({
    alkhattabaUUID,
    userUUID,
    alkhattabaText,
    conversationID,
    userID,
    conversationType,
    oneOOneCId
  }: {
    alkhattabaUUID: string;
    userUUID: string;
    alkhattabaText: string;
    conversationID: string;
    userID: number;
    conversationType: TalkhattabatConversationType;
    oneOOneCId: string;
  }) => {
    this.conversationsProps.get(conversationID).messageField = "";
    if (
      !isDefinedString(alkhattabaUUID) ||
      !isDefinedString(userUUID) ||
      !isDefinedString(alkhattabaText)
    ) {
      return;
    }
    const { status, code, data } = await apiCallSendOneOOneMessage({
      alkhattabaUUID,
      userUUID,
      alkhattabaText,
      userID,
      conversationType,
      isAiMessage: getCorePersonalStore().isKhattabaAiAdmin.get()
    });
    if (status === "OK") {
      const databaseCurrentTime = reduce(
        () => data.messageCurrentTime,
        DEFAULT_TIME
      );
      this.handleSendOneOOneMessageClient({
        message: alkhattabaText,
        messageType: "text",
        conversationID,
        databaseCurrentTime,
        alkhattabaUUID,
        userUUID,
        userID,
        conversationType,
        oneOOneCId
      });
      this.markConversationAsReadOnMessageSend({
        alkhattabaUUID,
        clientUUID: userUUID,
        clientID: userID,
        conversationType
      });
    } else {
      getCorePopUpStore().showAlertMessage(code);
    }
  };

  createOneOTwo = async ({
    alkhattabaUUID,
    userUUID,
    userID,
    matchUserId,
    conversationID,
    oneOOneCId
  }: {
    alkhattabaUUID: string;
    userUUID: string;
    userID: number;
    matchUserId: number;
    conversationID: string;
    oneOOneCId: string;
  }) => {
    if (
      !isDefinedString(alkhattabaUUID) ||
      !isDefinedString(userUUID) ||
      !isPositiveNumber(matchUserId)
    ) {
      return;
    }
    const { status, code } = await apiCallCreateOneOTwo({
      alKhattabaUUID: alkhattabaUUID,
      userUUID,
      userID,
      matchUserId
    });
    if (status === "OK") {
      runInAction(() => {
        this.conversationsProps.get(conversationID).insertUserIdField = -1;
      });
      this.getClientConversations({
        alkhattabaUUID,
        userUUID,
        shouldLoad: true,
        oneOOneCId
      });
    } else {
      getCorePopUpStore().showAlertMessage(code);
    }
  };

  handleSendOneOTwoMessageClient = ({
    message,
    conversationID,
    databaseCurrentTime,
    alkhattabaUUID,
    userUUID,
    userID,
    oneOTwoConversationId,
    type,
    oneOOneCId
  }: {
    message: string;
    conversationID: string;
    databaseCurrentTime: string;
    alkhattabaUUID: string;
    userUUID: string;
    userID: number;
    oneOTwoConversationId: string;
    type: TMessagesType;
    oneOOneCId: string;
  }) => {
    runInAction(() => {
      const clientConversationID = oneOOneCId;
      const isOneOTwoonversationExist = !isNullOrUndefined(
        this.clientConversations.get(clientConversationID)
      );
      const oneOTwoMessageToAdd: TKhattabatMessage = {
        isKhattabaMessage: 1,
        isClientMessage: 0,
        message,
        messageType: type,
        createdOn: databaseCurrentTime,
        isSeen: 1,
        senderGender: "f",
        conversationId: conversationID
      };
      if (!isOneOTwoonversationExist) {
        this.clientConversations.set(clientConversationID, {
          oneOOneCMessages: [],
          oneOOneCComments: [],
          oneOTwoMessages: {
            oneOTwoConversationId: {
              exitByMatch: 1,
              exitByUser: 1,
              exitedByClientDate: createDateFromSpecificDateString(""),
              exitedByMatchDate: createDateFromSpecificDateString(""),
              peerID: -1,
              peerUUID: "",
              messages: [],
              matchUserName: "",
              manualIsQuality: 0,
              automaticIsQuality: 1
            }
          },
          alKhattabaUUID: alkhattabaUUID,
          userUUID,
          userID,
          shouldShowConversations: true,
          oneOOneCId: ""
        });
      }
      this.conversationsProps.get(conversationID).messageField = "";
      this.clientConversations
        .get(clientConversationID)
        .oneOTwoMessages[
          oneOTwoConversationId
        ].messages.push(oneOTwoMessageToAdd);
    });
  };

  sendOneTwoMessageDatabase = async ({
    alkhattabaUUID,
    userUUID,
    userID,
    alkhattabaText,
    peerID,
    peerUUID,
    conversationID,
    oneOTwoConversationId,
    oneOOneCId
  }: {
    alkhattabaUUID: string;
    userUUID: string;
    userID: number;
    alkhattabaText: string;
    peerID: number;
    peerUUID: string;
    conversationID: string;
    oneOTwoConversationId: string;
    oneOOneCId: string;
  }) => {
    if (
      !isDefinedString(alkhattabaUUID) ||
      !isDefinedString(userUUID) ||
      !isDefinedString(alkhattabaText)
    ) {
      return;
    }
    const { status, code, data } = await apiCallSendOneOTwoMessage({
      alkhattabaUUID,
      clientUUID: userUUID,
      clientUserID: userID,
      alkhattabaText,
      matchUUID: peerUUID,
      matchID: peerID
    });
    if (status === "OK") {
      const databaseCurrentTime = reduce(() => data.currentTime, DEFAULT_TIME);

      this.handleSendOneOTwoMessageClient({
        message: alkhattabaText,
        conversationID,
        databaseCurrentTime,
        alkhattabaUUID,
        userUUID,
        userID: peerID,
        oneOTwoConversationId,
        type: "text",
        oneOOneCId
      });
    } else {
      getCorePopUpStore().showAlertMessage(code);
    }
  };

  getClientInformationsAndIndex = ({ userID }: { userID: number }) => {
    const data = this.getClients.get();
    const targetedUserIndex = data.findIndex((userData) => {
      return userData.userID === userID;
    });
    const userData = data[targetedUserIndex];
    return {
      userData: {
        isBlocked: reduce(() => userData.isBlocked, 0),
        isDeleted: reduce(() => userData.isDeleted, 0),
        createdOn: reduce(() => userData.createdOn, ""),
        oneOOneCId: reduce(() => userData.oneOOneCId, ""),
        alkhattabaUUID: reduce(() => userData.alkhattabaUUID, ""),
        isVIPKhatabat: reduce(
          () => (userData.vipAlkhattabatStatus === "EXPIRED" ? false : true),
          false
        ),
        userUUID: reduce(() => userData.userUUID, ""),
        userID: reduce(() => userData.userID, -1),
        userName: reduce(() => userData.userName, ""),
        originCountry: reduce(() => userData.originCountry, ""),
        residenceCoutry: reduce(() => userData.residenceCoutry, ""),
        originCountryCode: reduce(() => userData.originCountryCode, ""),
        residenceCountryCode: reduce(() => userData.residenceCountryCode, ""),
        height: reduce(() => userData.height, 0),
        weight: reduce(() => userData.weight, 0),
        age: reduce(() => userData.age, 0),
        marriageType: reduce(() => userData.marriageType, ""),
        unreadOneOOneCMessages: reduce(
          () => userData.unreadOneOOneCMessages,
          0
        ),
        isOneOOneCHiddenByKhattaba: reduce(
          () => userData.isOneOOneCHiddenByKhattaba,
          false
        ),
        isRenewedInLastSevenDays: reduce(
          () => userData.isRenewedInLastSevenDays,
          false
        ),
        isOneOOneCHasBeenReactivated: reduce(
          () => userData.hasBeenReactivated,
          1
        ),
        oneOOneCReactivateDate: reduce(
          () => userData.reactivatedAt,
          getDefaultDate()
        ),
        hasUnreadMessagesSinceMoreThan18Hours: reduce(
          () => userData.hasUnreadMessagesSinceMoreThan18Hours,
          false
        ),
        hasNoRCMessageSinceMoreThan54Hours: reduce(
          () => userData.hasNoRCMessageSinceMoreThan54Hours,
          false
        ),
        clientIsAlmostExpiredAndNo102Quality: reduce(
          () => userData.clientIsAlmostExpiredAndNo102Quality,
          false
        ),
        vipKhattabaStatus: reduce(
          () => userData.vipAlkhattabatStatus,
          "EXPIRED"
        ),
        oneOTwoCounter: reduce(() => userData.oneOTwoCounter, 0)
      },
      targetedUserIndex
    };
  };

  getClients = computed((): TGetKhattabaClients[] => {
    const isAiKhattaba = getCorePersonalStore().isKhattabaAiAdmin.get();
    if (isAiKhattaba) {
      return getKhattabaAiStore().clients.get();
    }

    const isAllClients = this.getClientPageType.get() === E_CLIENT_PAGE.ALL;
    if (!isAllClients) {
      return this.getRequiresAttentionClients.get();
    }

    if (getKhattabatFilterstore().searchingClients.get()) {
      return getKhattabatFilterstore().filteredArray.get();
    }
    return this.clients.get();
  });

  getCurrentUserIds = () => {
    const currentUsersId = this.getClients.get().map((client) => {
      return client.userID;
    });
    return currentUsersId;
  };

  updateBreachLevelClientStats = ({
    alkhattabaUUID,
    clientUUID,
    booleanKey,
    booleanKeyNewValue
  }: {
    alkhattabaUUID: string;
    clientUUID: string;
    booleanKey:
      | "hasUnreadMessagesSinceMoreThan18Hours"
      | "hasNoRCMessageSinceMoreThan54Hours"
      | "clientIsAlmostExpiredAndNo102Quality";
    booleanKeyNewValue: boolean;
  }) => {
    const updatedClients = this.clients.get().map((client) => {
      if (
        client.alkhattabaUUID === alkhattabaUUID &&
        client.userUUID === clientUUID
      ) {
        runInAction(() => {
          client[booleanKey] = booleanKeyNewValue;
        });
      }
      return client;
    });

    this.setKhattaClients(updatedClients);
  };

  setOneOOneMessagesSeenClientSide = ({
    alkhattabaUUID,
    clientUUID,
    clientID,
    pageType
  }: {
    alkhattabaUUID: string;
    clientUUID: string;
    clientID: number;
    pageType: TSelectionPages;
  }) => {
    switch (pageType) {
      case "clientPage":
        const updatedClients = this.clients.get().map((client) => {
          if (
            client.alkhattabaUUID === alkhattabaUUID &&
            client.userUUID === clientUUID
          ) {
            runInAction(() => {
              client.unreadOneOOneCMessages = 0;
            });
          }
          return client;
        });
        if (getKhattabatFilterstore().isSearchingClients.get()) {
          return getKhattabatFilterstore().setFilteredArray(updatedClients);
        }
        return this.setKhattaClients(updatedClients);

      case "matchResultPage":
        const index =
          getKhattabatResultsStore().findMatchIndexInConversationArray({
            parameter: "matchID",
            parameterValue: clientID,
            matchesConversations:
              getKhattabatResultsStore().matchesConversations.get()
          });
        runInAction(() => {
          getKhattabatResultsStore().matchesConversations.get()[
            index
          ].matchUnreadMessages = 0;
        });
    }
  };

  setOneOOneMessagesSeen = async ({
    alkhattabaUUID,
    clientUUID,
    clientID,
    pageType
  }: {
    alkhattabaUUID: string;
    clientUUID: string;
    clientID: number;
    pageType: TSelectionPages;
  }) => {
    const { status, code } = await apiCallSetOneOOneMessagesSeen({
      alkhattabaUUID,
      clientUUID,
      conversationType: pageType === "matchResultPage" ? "101M" : "101C"
    });
    if (status === "OK") {
      this.setOneOOneMessagesSeenClientSide({
        alkhattabaUUID,
        clientUUID,
        clientID,
        pageType
      });
      this.updateBreachLevelClientStats({
        alkhattabaUUID,
        clientUUID,
        booleanKey: "hasUnreadMessagesSinceMoreThan18Hours",
        booleanKeyNewValue: false
      });
    } else {
      getCorePopUpStore().showAlertMessage(code);
    }
  };

  markConversationAsReadOnMessageSend = async ({
    alkhattabaUUID,
    clientUUID,
    clientID,
    conversationType
  }: {
    alkhattabaUUID: string;
    clientUUID: string;
    clientID: number;
    conversationType: TalkhattabatConversationType;
  }) => {
    const isOneOneCComments = conversationType === "101C_COMMENTS";
    if (isOneOneCComments) {
      return;
    }
    const isOneOOneMConversation = conversationType === "101M";
    const pageType: TSelectionPages = isOneOOneMConversation
      ? "matchResultPage"
      : "clientPage";

    this.setOneOOneMessagesSeen({
      alkhattabaUUID,
      clientUUID,
      clientID,
      pageType
    });
  };

  handleConversationPropsBetweenPages = ({
    conversationType,
    clientConversationID,
    oneOTwoConversationId,
    userUUID,
    conversationIndex,
    isOneOOneCHiddenByKhattaba
  }: {
    conversationType: TalkhattabatConversationType;
    clientConversationID: string;
    oneOTwoConversationId: string;
    userUUID: string;
    conversationIndex: number;
    isOneOOneCHiddenByKhattaba: boolean;
  }) => {
    const isOneOneCConversation = conversationType === "101C";
    const isOneOneCComments = conversationType === "101C_COMMENTS";

    const conversation = this.clientConversations.get(clientConversationID);
    const messages = isOneOneCConversation
      ? reduce(() => conversation.oneOOneCMessages, [])
      : isOneOneCComments
        ? reduce(() => conversation.oneOOneCComments, [])
        : reduce(
            () => conversation.oneOTwoMessages[oneOTwoConversationId].messages,
            []
          );

    const oneOOnePeerId = reduce(
      () => this.clientConversations.get(clientConversationID).userID,
      -1
    );
    const peerUUID = isOneOneCConversation
      ? userUUID
      : reduce(
          () => conversation.oneOTwoMessages[oneOTwoConversationId].peerUUID,
          ""
        );
    const peerID = isOneOneCConversation
      ? oneOOnePeerId
      : reduce(
          () => conversation.oneOTwoMessages[oneOTwoConversationId].peerID,
          -1
        );

    const exitByMatch = isOneOneCConversation
      ? 0
      : reduce(
          () => conversation.oneOTwoMessages[oneOTwoConversationId].exitByMatch,
          0
        );

    const exitByClient = isOneOneCConversation
      ? 0
      : reduce(
          () => conversation.oneOTwoMessages[oneOTwoConversationId].exitByUser,
          0
        );

    const exitedByMatchDate = isOneOneCConversation
      ? createDateFromSpecificDateString("")
      : reduce(
          () =>
            conversation.oneOTwoMessages[oneOTwoConversationId]
              .exitedByMatchDate,
          createDateFromSpecificDateString("")
        );
    const exitedByClientDate = isOneOneCConversation
      ? createDateFromSpecificDateString("")
      : reduce(
          () =>
            conversation.oneOTwoMessages[oneOTwoConversationId]
              .exitedByClientDate,
          createDateFromSpecificDateString("")
        );

    const matchName = isOneOneCConversation
      ? ""
      : reduce(
          () =>
            conversation.oneOTwoMessages[oneOTwoConversationId].matchUserName,
          ""
        );
    const reducedCaseNullConvId = isOneOneCConversation
      ? clientConversationID
      : `${clientConversationID}_${conversationIndex}`;
    const conversationID = reduce(
      () => messages[0].conversationId,
      reducedCaseNullConvId
    );
    this.createConversationProps(conversationID);
    const isConvExitedByMatch = isPositiveNumber(exitByMatch);
    const isConvExitedByClient = isPositiveNumber(exitByClient);
    const isPendingInteraction = !isDefinedArray(messages);
    const shouldNotShow =
      conversationType === "101C" && isOneOOneCHiddenByKhattaba;

    return {
      isOneOneCConversation,
      conversationID,
      isConvExitedByMatch,
      isConvExitedByClient,
      exitedByMatchDate,
      exitedByClientDate,
      isPendingInteraction,
      messages,
      peerUUID,
      peerID,
      shouldNotShow,
      oneOOnePeerId,
      matchName
    };
  };

  sendOneOOneMediaDataBase = async ({
    conversationID,
    alkhattabaUUID,
    userUUID,
    conversationType,
    userID,
    type,
    oneOOneCId
  }: {
    alkhattabaUUID: string;
    userUUID: string;
    userID: number;
    conversationID: string;
    peerID: number;
    peerUUID: string;
    conversationType: TalkhattabatConversationType;
    type: TMediaType;
    oneOOneCId: string;
  }) => {
    const { data, status, code } = await apiCallSendOneOOneMidea({
      alkhattabaUUID,
      userUUID,
      userID,
      conversationType,
      type,
      attachment: this.imageToBeUploadedBase64.get(),
      duration: 0
    });
    if (status === "OK") {
      const databaseCurrentTime = reduce(
        () => data.messageCurrentTime,
        DEFAULT_TIME
      );

      const mediaMessage = reduce(() => data.mideaMessage, "");
      this.handleSendOneOOneMessageClient({
        message: mediaMessage,
        messageType: type,
        conversationID,
        databaseCurrentTime,
        alkhattabaUUID,
        userUUID,
        userID,
        conversationType,
        oneOOneCId
      });

      this.markConversationAsReadOnMessageSend({
        alkhattabaUUID,
        clientUUID: userUUID,
        clientID: userID,
        conversationType
      });
    } else {
      getCorePopUpStore().showAlertMessage(code);
    }
  };

  sendOneOTwoMediaDataBase = async ({
    conversationID,
    oneOTwoConversationId,
    alkhattabaUUID,
    userUUID,
    userID,
    peerID,
    peerUUID,
    type,
    oneOOneCId
  }: {
    alkhattabaUUID: string;
    userUUID: string;
    userID: number;
    conversationID: string;
    oneOTwoConversationId: string;
    peerID: number;
    peerUUID: string;
    type: TMediaType;
    oneOOneCId: string;
  }) => {
    const { status, code, data } = await apiCallSendOneOTwoMidea({
      alkhattabaUUID,
      clientUUID: userUUID,
      clientUserID: userID,
      matchUUID: peerUUID,
      matchID: peerID,

      type,
      attachment: this.imageToBeUploadedBase64.get(),
      duration: 0
    });
    if (status === "OK") {
      const databaseCurrentTime = reduce(
        () => data.messageCurrentTime,
        DEFAULT_TIME
      );

      const mediaMessage = reduce(() => data.mideaMessage, "");

      this.handleSendOneOTwoMessageClient({
        message: mediaMessage,
        conversationID,
        databaseCurrentTime,
        alkhattabaUUID,
        userUUID,
        userID: peerID,
        oneOTwoConversationId,
        type,
        oneOOneCId
      });
    } else {
      getCorePopUpStore().showAlertMessage(code);
    }
  };

  handleImageUpload = async ({
    conversationID,
    alkhattabaUUID,
    userUUID,
    conversationType,
    userID,
    peerID,
    peerUUID,
    oneOTwoConversationId,
    oneOOneCId
  }: {
    alkhattabaUUID: string;
    userUUID: string;
    userID: number;
    conversationID: string;
    peerID: number;
    peerUUID: string;
    conversationType: TalkhattabatConversationType;
    oneOTwoConversationId: string;
    oneOOneCId: string;
  }) => {
    try {
      // Open file dialog and allow the user to select an image file
      const file = await fileOpen({
        mimeTypes: ["image/*"], // Restrict to image files
        extensions: ALLOWED_UPLOADED_IMAGES_TYPES, // Common image extensions
        multiple: false
      });

      // Read the file as a Data URL (base64 encoded)
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = async () => {
        if (typeof reader.result === "string") {
          const initialImageSizeInMb = imageHelper.getImageFileSize(
            reader.result
          );
          let resizedImageBase64 = reader.result;
          if (initialImageSizeInMb > MAX_UPLOADED_IMAGE_SIZE_IN_MB) {
            resizedImageBase64 = await imageHelper.resizeBase64Image(
              reader.result
            );
          }
          this.setImageToBeUploadedBase64(resizedImageBase64);

          const isOneOTwoonversation = conversationType === "102";
          if (isOneOTwoonversation) {
            this.sendOneOTwoMediaDataBase({
              conversationID,
              alkhattabaUUID,
              userUUID,
              userID,
              peerID,
              peerUUID,
              oneOTwoConversationId,
              type: "picture",
              oneOOneCId
            });
          } else {
            const reducedUserUUID = reduce(() => userUUID, "");
            await this.sendOneOOneMediaDataBase({
              conversationID,
              alkhattabaUUID,
              userUUID: reducedUserUUID,
              conversationType,
              userID,
              peerID,
              peerUUID,
              type: "picture",
              oneOOneCId
            });
          }
        }
      };
    } catch (error) {}
  };

  async reativateOneOTwoConversation() {
    const alkhattabaUUID =
      this.reactivateOneOTwoConversationProps.get().alkhattabaUUID;
    const userUUID = this.reactivateOneOTwoConversationProps.get().userUUID;
    const conversationID =
      this.reactivateOneOTwoConversationProps.get().conversationID;
    const oneOOneCId = this.reactivateOneOTwoConversationProps.get().oneOOneCId;

    if (
      !isDefinedString(alkhattabaUUID) ||
      !isDefinedString(userUUID) ||
      !isDefinedString(conversationID)
    ) {
      return;
    }
    getCoreUIStore().setIsLoading("KHATTABAT_CLIENTS", true);
    const { status, code } = await apiCallReactivateOneOTwo({
      oneOTwoConversationID: conversationID
    });
    getCoreUIStore().setIsLoading("KHATTABAT_CLIENTS", false);
    if (status === "OK") {
      await this.getClientConversations({
        alkhattabaUUID,
        userUUID,
        shouldLoad: true,
        oneOOneCId
      });
    } else {
      getCorePopUpStore().showAlertMessage(code);
    }
  }

  get102ConversationsMessagesCounters({
    clientConversationID,
    oneOTwoConversationId
  }: {
    clientConversationID: string;
    oneOTwoConversationId: string;
  }) {
    const conversation = this.clientConversations.get(clientConversationID);
    const messages = reduce(
      () => conversation.oneOTwoMessages[oneOTwoConversationId].messages,
      []
    );
    let khattabaMessagesCounter = 0;
    let clientMessagesCounter = 0;
    let matchMessagesCounter = 0;
    if (isDefinedArray(messages)) {
      messages.forEach((message) => {
        if (isPositiveNumber(message.isKhattabaMessage)) {
          khattabaMessagesCounter++;
        } else if (isPositiveNumber(message.isClientMessage)) {
          clientMessagesCounter++;
        } else {
          matchMessagesCounter++;
        }
      });
    }
    return {
      khattabaMessagesCounter,
      clientMessagesCounter,
      matchMessagesCounter
    };
  }

  getOneTwoQualityState({
    clientConversationID,
    oneOTwoConversationId
  }: {
    clientConversationID: string;
    oneOTwoConversationId: string;
  }) {
    const conversation = this.clientConversations.get(clientConversationID);
    let qualityState = reduce(
      () =>
        isPositiveNumber(
          conversation.oneOTwoMessages[oneOTwoConversationId].manualIsQuality
        ),
      false
    );
    return qualityState;
  }

  async toggleOneTwoQualityState({
    clientConversationID,
    oneOTwoConversationId,
    oldState,
    isAuto,
    clientID
  }: {
    clientConversationID: string;
    oneOTwoConversationId: string;
    oldState: boolean;
    isAuto: number;
    clientID: number;
  }) {
    const newNumberState = oldState ? 0 : 1;
    const conversation = this.clientConversations.get(clientConversationID);

    getCoreUIStore().setIsLoading("KHATTABAT_CLIENTS", true);
    const { status, code } = await apiCallUpdateOneOTwoQuality({
      oneOTwoConversationID: oneOTwoConversationId,
      newState: newNumberState,
      isAuto
    });
    getCoreUIStore().setIsLoading("KHATTABAT_CLIENTS", false);

    if (status === "OK") {
      runInAction(() => {
        conversation.oneOTwoMessages[oneOTwoConversationId].manualIsQuality =
          newNumberState;
        if (isPositiveNumber(isAuto)) {
          conversation.oneOTwoMessages[
            oneOTwoConversationId
          ].automaticIsQuality = 1;
        }

        this.handleClientIsAlmostExpiredAndNo102QualityUpdate({
          clientID,
          clientConversationID
        });
      });
    } else {
      getCorePopUpStore().showAlertMessage(code);
    }
  }

  handleClientIsAlmostExpiredAndNo102QualityUpdate({
    clientID,
    clientConversationID
  }: {
    clientID: number;
    clientConversationID: string;
  }) {
    const targetedClientData = this.getClientInformationsAndIndex({
      userID: clientID
    }).userData;

    const isAlmostExpiredUser =
      targetedClientData.vipKhattabaStatus === "ALMOST_EXPIRED";
    if (!isAlmostExpiredUser) {
      return;
    }

    const conversations =
      getKhattabatStore().clientConversations.get(clientConversationID);

    const oneOTwoMessagesMatchesObject = reduce(
      () => conversations.oneOTwoMessages,
      {}
    );

    const oneOTwoConversationsIds = Object.keys(oneOTwoMessagesMatchesObject);
    const atLeastOneOneOTwoQualityHasLeft = oneOTwoConversationsIds.some(
      (oneOTwoConversationId) => {
        return conversations.oneOTwoMessages[oneOTwoConversationId]
          .manualIsQuality;
      }
    );

    this.updateBreachLevelClientStats({
      alkhattabaUUID: targetedClientData.alkhattabaUUID,
      clientUUID: targetedClientData.userUUID,
      booleanKey: "clientIsAlmostExpiredAndNo102Quality",
      booleanKeyNewValue: !atLeastOneOneOTwoQualityHasLeft
    });
  }

  async shouldSetOneOTwoToQuality({
    clientConversationID,
    oneOTwoConversationId,
    clientID
  }: {
    clientConversationID: string;
    oneOTwoConversationId: string;
    clientID: number;
  }) {
    const conversation = this.clientConversations.get(clientConversationID);
    const alreadyUpdatedByAuto = reduce(
      () =>
        isPositiveNumber(
          conversation.oneOTwoMessages[oneOTwoConversationId].automaticIsQuality
        ),
      false
    );

    if (alreadyUpdatedByAuto) {
      return false;
    }

    const { clientMessagesCounter, matchMessagesCounter } =
      this.get102ConversationsMessagesCounters({
        clientConversationID,
        oneOTwoConversationId
      });

    const isQualityMatchMessagesCounter =
      matchMessagesCounter >= KHATTABAT_ONE_O_TWO_MIN_QUALITY_MESSAGES;

    const isQualityClientMessagesCounter =
      clientMessagesCounter >= KHATTABAT_ONE_O_TWO_MIN_QUALITY_MESSAGES;

    const manualIsQuality =
      isQualityMatchMessagesCounter && isQualityClientMessagesCounter;

    if (manualIsQuality) {
      await this.toggleOneTwoQualityState({
        clientConversationID,
        oneOTwoConversationId,
        oldState: false,
        isAuto: 1,
        clientID
      });
    }
    return true;
  }

  getKhattabaClientToReactivateDetails = ({
    userID
  }: {
    userID: number;
  }): {
    hasBeenReactivated: boolean;
    stringifiedReactivatedAt: string;
    shouldShowReactivationProcess: boolean;
  } => {
    const targetedClientData = this.getClientInformationsAndIndex({
      userID
    }).userData;

    const clientIsAlreadyHasBeenReactivated = isPositiveNumber(
      targetedClientData.isOneOOneCHasBeenReactivated
    );
    if (clientIsAlreadyHasBeenReactivated) {
      return {
        hasBeenReactivated: true,
        stringifiedReactivatedAt: `${targetedClientData.oneOOneCReactivateDate}`,
        shouldShowReactivationProcess: true
      };
    }

    return {
      hasBeenReactivated: false,
      stringifiedReactivatedAt: DEFAULT_TIME,
      shouldShowReactivationProcess: true
    };
  };

  requestReactivatingKhattabaClient({
    clientConversationID,
    userID,
    alkhattabaUUID,
    clientUUID
  }: {
    clientConversationID: string;
    userID: number;
    alkhattabaUUID: string;
    clientUUID: string;
  }) {
    //for more safety in case Khattaba opened affirmation Popup before this
    getCorePopUpStore().closeConfirmationPopUps();

    const conversation = this.clientConversations.get(clientConversationID);
    const messages = reduce(() => conversation.oneOOneCMessages, []);
    const clientInformation = this.getClientInformationsAndIndex({
      userID
    });
    const isVIPKhatabat = clientInformation.userData.isVIPKhatabat;

    if (!isDefinedArray(messages) || !isVIPKhatabat) {
      return getCorePopUpStore().showAlertMessage(
        "CANNOT_REACTIVATE_KHATTABA_CLIENT_POPUP"
      );
    }

    const clientMessages = messages.filter((message) => {
      return (
        isPositiveNumber(message.isClientMessage) &&
        !isPositiveNumber(message.isKhattabaMessage)
      );
    });
    //arr.at(-1) ==> get last element in the array
    const lastMessageDate = reduce(() => clientMessages.at(-1).createdOn, "");
    const dayDifferenceForLatestMessageSentByUser = getDaysDifference({
      dateFrom: createDateFromSpecificDateString(lastMessageDate),
      dateTo: createDateFromSpecificDateString("")
    });
    const canNotBeReactivatedYet =
      KHATTABAT_REACTIVATION_DELAY_DAYS >
      dayDifferenceForLatestMessageSentByUser;

    if (canNotBeReactivatedYet) {
      return getCorePopUpStore().showAlertMessage(
        "CANNOT_REACTIVATE_KHATTABA_CLIENT_POPUP"
      );
    }

    this.setClientToBeReactivatedDetails({
      userID,
      alkhattabaUUID,
      clientConversationID,
      clientUUID
    });
    return getCorePopUpStore().openConfirmationPopUps(
      "REACTIVATE_KHATTABA_CLIENT_POPUP"
    );
  }

  setKhattabaClientTobeReactivatedClientSide({
    alkhattabaUUID,
    clientUUID,
    newReactivationAtDate
  }: {
    alkhattabaUUID: string;
    clientUUID: string;
    newReactivationAtDate: Date;
  }) {
    const updatedClients = this.clients.get().map((client) => {
      if (
        client.alkhattabaUUID === alkhattabaUUID &&
        client.userUUID === clientUUID
      ) {
        runInAction(() => {
          client.hasBeenReactivated = 1;
          client.reactivatedAt = newReactivationAtDate;
        });
      }
      return client;
    });

    if (getKhattabatFilterstore().isSearchingClients.get()) {
      return getKhattabatFilterstore().setFilteredArray(updatedClients);
    }
    return this.setKhattaClients(updatedClients);
  }

  async confirmeRactivatingKhatabaClient() {
    const shouldBeDisabled =
      getCoreUIStore().isPageLoading.get("KHATTABAT_CLIENTS");
    if (shouldBeDisabled) {
      return;
    }
    const { alkhattabaUUID, clientUUID, clientConversationID, userID } =
      this.clientToBeReactivatedDetails.get();
    getCoreUIStore().setIsLoading("KHATTABAT_CLIENTS", true);
    const { data, status, code } = await apiCallReactivateKhatabaClient({
      oneOOneCId: clientConversationID,
      userID,
      alkhattabaUUID: alkhattabaUUID
    });
    if (status === "OK") {
      const reactivateSentDateReduced = reduce(
        () => data.ReactivationSentDate,
        getDefaultDate()
      );
      this.setKhattabaClientTobeReactivatedClientSide({
        alkhattabaUUID,
        clientUUID,
        newReactivationAtDate: reactivateSentDateReduced
      });
    } else {
      getCorePopUpStore().showAlertMessage(code);
    }
    getCoreUIStore().setIsLoading("KHATTABAT_CLIENTS", false);
    getCorePopUpStore().closeConfirmationPopUps();
  }
}

const getKhattabatStore = memoize(
  () => new KhattabatStore(),
  () => 1
);
export default getKhattabatStore;
